import { useCallback, useEffect, useRef, useState } from 'react';
import { DEFAULT_PAGE_SIZE, type Paginated } from 'shared';
import { api, ApiError } from '../lib/api';

/** Long enough that a typed word is one request, short enough to feel immediate. */
const DEBOUNCE_MS = 300;

interface Options {
  /** Extra query parameters. Falsy values are dropped rather than sent as empty. */
  params?: Record<string, string | null | undefined>;
  pageSize?: number;
  /**
   * False while a required parameter is still missing — no request goes out, and the list
   * reports itself as still loading, because from the reader's side the data is on its way.
   */
  enabled?: boolean;
}

export interface PagedList<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  /** The first and last row numbers on this page, 1-based. `[0, 0]` when empty. */
  range: [number, number];
  /** What is in the search box right now; it reaches the server after the debounce. */
  term: string;
  setTerm: (v: string) => void;
  /** True when a term has been applied — an empty result then means "no match", not "no data". */
  filtered: boolean;
  setPage: (p: number) => void;
  loading: boolean;
  error: string | null;
  /** Re-fetch the current page, for example after an edit. */
  reload: () => void;
  /** Back to page one and re-fetch, for after a create: new rows sort to the top. */
  refresh: () => void;
}

/**
 * One page of a server-paged list.
 *
 * Paging and searching are the SERVER's job here — the browser never holds the whole
 * table. That is the difference that matters once a list is long: a client-side filter
 * over a `pageSize=100` fetch looks identical on a demo database and then silently stops
 * finding rows 101 and up.
 *
 * The endpoint must accept `page`, `pageSize`, and `q`, and answer with `Paginated<T>`.
 */
export function usePagedList<T>(path: string, opts: Options = {}): PagedList<T> {
  const pageSize = opts.pageSize ?? DEFAULT_PAGE_SIZE;
  const enabled = opts.enabled ?? true;

  const [term, setTerm] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [nonce, setNonce] = useState(0);
  const [items, setItems] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Serialised so the fetch can depend on the VALUES; the caller passes a fresh object
  // literal on every render and depending on its identity would loop forever.
  const paramKey = JSON.stringify(opts.params ?? {});

  // Changing a filter invalidates the page number: page 3 of the previous filter is
  // rarely page 3 of the new one, and is often past the end. Adjusting during render
  // rather than in an effect keeps it to a single fetch.
  const [appliedParams, setAppliedParams] = useState(paramKey);
  if (paramKey !== appliedParams) {
    setAppliedParams(paramKey);
    setPage(1);
  }

  useEffect(() => {
    if (term === q) return;
    const t = setTimeout(() => { setQ(term); setPage(1); }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [term, q]);

  /** Only the newest request may write state; an earlier one landing late is discarded. */
  const seq = useRef(0);

  const fetchPage = useCallback(async () => {
    // Still waiting on a prerequisite. `loading` stays true so the table says "Memuat…"
    // rather than flashing "no rows" for data nobody has asked for yet.
    if (!enabled) {
      setItems([]); setTotal(0); setError(null);
      return;
    }

    const mine = ++seq.current;
    setLoading(true);

    const search = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (q) search.set('q', q);
    const extra = JSON.parse(paramKey) as Record<string, string | null | undefined>;
    for (const [k, v] of Object.entries(extra)) {
      if (v) search.set(k, v);
    }

    try {
      const res = await api.get<Paginated<T>>(`${path}?${search}`);
      if (mine !== seq.current) return;
      setItems(res.items);
      setTotal(res.total);
      setError(null);
    } catch (err) {
      if (mine !== seq.current) return;
      setItems([]);
      setTotal(0);
      setError(err instanceof ApiError ? err.message : 'Gagal memuat data.');
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [path, page, pageSize, q, paramKey, enabled, nonce]);

  useEffect(() => { void fetchPage(); }, [fetchPage]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const range: [number, number] = items.length === 0
    ? [0, 0]
    : [(page - 1) * pageSize + 1, (page - 1) * pageSize + items.length];

  return {
    items, total, page, pageSize, totalPages, range,
    term, setTerm, filtered: q !== '',
    setPage: (p: number) => setPage(Math.max(1, p)),
    loading, error,
    reload: () => setNonce((n) => n + 1),
    refresh: () => { setPage(1); setNonce((n) => n + 1); },
  };
}
